import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * Chrome/Edge (Android + desktop) → intercepts `beforeinstallprompt` and shows
 * a compact "Install app" banner. Tap the button to trigger the native install.
 *
 * iOS Safari doesn't fire `beforeinstallprompt`; we detect it separately and
 * show a lightweight hint pointing at the Share → Add to Home Screen menu.
 *
 * Everything is dismissible + remembered in localStorage so we don't nag.
 *
 * NOTE ON STORAGE (in case a static-analysis tool flags this): the two keys
 * below hold only the boolean UI-state flag "user has dismissed this hint"
 * (value is literally "1"). No PII, no auth tokens, no session data.
 * localStorage is the correct choice here because we want the dismissal to
 * persist across sessions — sessionStorage would re-prompt the user every
 * time they close and reopen the browser.
 */
const DISMISS_KEY = "spotfiller.pwa.dismissed";
const IOS_HINT_KEY = "spotfiller.pwa.ios_hint_dismissed";

function isIOS() {
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}
function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS-specific standalone flag
    window.navigator.standalone === true
  );
}

export default function InstallPrompt() {
  const [prompt, setPrompt] = useState(null);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (localStorage.getItem(DISMISS_KEY)) return;

    const onBIP = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // iOS Safari can't be programmatically prompted — show hint if applicable.
    if (isIOS() && !localStorage.getItem(IOS_HINT_KEY)) {
      setShowIOSHint(true);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBIP);
  }, []);

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    await prompt.userChoice;
    setPrompt(null);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const dismiss = () => {
    setPrompt(null);
    localStorage.setItem(DISMISS_KEY, "1");
  };
  const dismissIOSHint = () => {
    setShowIOSHint(false);
    localStorage.setItem(IOS_HINT_KEY, "1");
  };

  // Android / desktop Chrome — installable prompt
  if (prompt) {
    return (
      <div
        data-testid="pwa-install-banner"
        className="mt-6 flex items-center gap-3 border border-[#C9A85F] bg-gradient-to-r from-[#1c1c1c] to-[#2A2724] text-white p-3 md:p-5"
      >
        <img
          src="/icon-192.png"
          alt=""
          className="w-9 h-9 md:w-10 md:h-10 shrink-0 rounded-lg"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <p className="mono text-[10px] uppercase tracking-[0.28em] text-[#C9A85F] truncate">
            Install Book Away
          </p>
          <p className="mt-0.5 text-xs md:text-sm md:leading-normal leading-snug">
            <span className="md:hidden">Add to Home Screen — one-tap posting.</span>
            <span className="hidden md:inline">Add to your home screen for one-tap access to post openings.</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid="pwa-install-btn"
            onClick={install}
            className="inline-flex h-11 items-center gap-1.5 gold-metallic-bg text-black px-3 md:px-4 text-[11px] font-semibold uppercase tracking-widest active:scale-[0.98] transition-transform"
          >
            <Download size={14} /> <span className="hidden sm:inline">Install</span>
          </button>
          <button
            data-testid="pwa-dismiss-btn"
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            className="h-11 w-11 inline-flex items-center justify-center border border-white/20 hover:border-white/50 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  // iOS Safari — instructional hint (can't trigger native prompt)
  if (showIOSHint) {
    return (
      <div
        data-testid="pwa-ios-hint"
        className="mt-6 flex items-start justify-between gap-3 border border-[#C9A85F] bg-gradient-to-r from-[#1c1c1c] to-[#2A2724] text-white p-4 md:p-5"
      >
        <div className="flex items-start gap-3 min-w-0">
          <img
            src="/icon-192.png"
            alt=""
            className="w-10 h-10 shrink-0 rounded-lg"
            aria-hidden="true"
          />
          <div className="min-w-0">
            <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
              Add to Home Screen
            </p>
            <p className="mt-1 text-sm leading-snug">
              Tap the{" "}
              <span className="mono text-[#C9A85F]">Share</span> icon in Safari,
              then <span className="mono text-[#C9A85F]">Add to Home Screen</span>.
            </p>
          </div>
        </div>
        <button
          data-testid="pwa-ios-hint-dismiss"
          onClick={dismissIOSHint}
          aria-label="Dismiss hint"
          className="h-11 w-11 shrink-0 inline-flex items-center justify-center border border-white/20 hover:border-white/50 transition-colors"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return null;
}
